import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/api';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';
import PageHeader, { CrumbLink } from '../../components/Layout/PageHeader';
import { statusPillSx, surfaceCardSx, tokens } from '../../theme/tokens';

function statusKind(status) {
  if (status === 'Active') return 'active';
  if (status === 'Pending') return 'pending';
  if (status === 'Suspended') return 'danger';
  return 'inactive';
}

function Kv({ label, children }) {
  return (
    <>
      <Typography component="dt" sx={{ color: tokens.muted, fontSize: 13.5 }}>
        {label}
      </Typography>
      <Typography component="dd" sx={{ fontWeight: 500, fontSize: 13.5, m: 0, fontVariantNumeric: 'tabular-nums' }}>
        {children}
      </Typography>
    </>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    fetchMember();
  }, [id]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/members/${id}`);
      setMember(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch member');
    } finally {
      setLoading(false);
    }
  };

  const submitPermanentDelete = async () => {
    if (!member) return;
    const expected = String(member.member_id || '').trim();
    if (deleteConfirmText.trim() !== expected) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/members/${member.id}`, {
        data: { confirmation: deleteConfirmText.trim() },
      });
      navigate('/dashboard/members');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete member');
      setDeleteOpen(false);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: tokens.primary }} />
      </Box>
    );
  }

  if (error || !member) {
    return (
      <Box>
        <Alert
          severity="error"
          sx={{
            borderRadius: '9px',
            bgcolor: tokens.dangerSoft,
            color: '#7C2E1D',
            border: '1px solid #EFCFC5',
            mb: 2,
          }}
        >
          {error || 'Member not found'}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/dashboard/members')}
          sx={{ borderColor: tokens.border, color: tokens.ink }}
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim();

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            <CrumbLink onClick={() => navigate('/dashboard/members')}>Members</CrumbLink>
            {' / '}
            <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>
              {fullName}
            </Box>
          </>
        }
        title={fullName}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/dashboard/members/${id}`)}
              sx={{ borderColor: tokens.border, color: tokens.ink }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                setDeleteConfirmText('');
                setDeleteOpen(true);
              }}
              sx={{ borderColor: '#EFCFC5', color: tokens.danger }}
            >
              Delete permanently
            </Button>
          </>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, textAlign: 'center', p: 3.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              <MemberProfileAvatar member={member} size={64} />
            </Box>
            <Typography
              sx={{
                fontFamily: tokens.fontDisplay,
                fontWeight: 600,
                fontSize: 17,
                color: tokens.ink,
              }}
            >
              {fullName}
            </Typography>
            <Typography
              sx={{
                color: tokens.muted,
                fontSize: 13,
                mt: 0.25,
                mb: 1.5,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {member.member_id}
            </Typography>
            <Box sx={statusPillSx(statusKind(member.status))}>
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'currentColor',
                }}
              />
              {member.status || 'Unknown'}
            </Box>
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr',
                gap: '8px 12px',
                mt: 2,
                textAlign: 'left',
                m: 0,
                pt: 1.75,
              }}
            >
              <Kv label="Phone">{member.phone || '—'}</Kv>
              <Kv label="Email">{member.email || '—'}</Kv>
              <Kv label="Gender">{member.gender || '—'}</Kv>
              <Kv label="Constituency">{member.constituency || '—'}</Kv>
              {member.region && <Kv label="Region">{member.region}</Kv>}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={surfaceCardSx}>
            <Box
              sx={{
                display: 'flex',
                gap: 0.25,
                borderBottom: `1px solid ${tokens.border}`,
                px: 2.5,
              }}
            >
              <Box
                sx={{
                  px: 1.75,
                  py: 1.5,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: tokens.ink,
                  borderBottom: `2px solid ${tokens.accent}`,
                }}
              >
                Overview
              </Box>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Typography
                sx={{
                  fontFamily: tokens.fontDisplay,
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1.25,
                  color: tokens.ink,
                }}
              >
                Membership summary
              </Typography>
              <Box
                component="dl"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '170px 1fr' },
                  gap: '8px 12px',
                  m: 0,
                }}
              >
                <Kv label="Member ID">
                  <span className="num">{member.member_id}</span>
                </Kv>
                <Kv label="Status">{member.status}</Kv>
                <Kv label="First name">{member.first_name}</Kv>
                <Kv label="Last name">{member.last_name}</Kv>
                <Kv label="Email">{member.email}</Kv>
                <Kv label="Phone">
                  <span className="num">{member.phone || '—'}</span>
                </Kv>
                <Kv label="Gender">{member.gender || '—'}</Kv>
                <Kv label="Constituency">{member.constituency || '—'}</Kv>
                {member.region && <Kv label="Region">{member.region}</Kv>}
              </Box>
              <Typography sx={{ mt: 2.5, color: tokens.muted, fontSize: 13 }}>
                Profile photo from registration or the member portal. Shown on admin cards and their ID card.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteSubmitting && setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', boxShadow: '0 20px 50px rgba(20,38,31,.25)' },
        }}
      >
        <DialogTitle sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 16 }}>
          Delete this member permanently?
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '9px', bgcolor: tokens.dangerSoft }}>
            This cannot be undone. The member will be removed from the platform and the database.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2, color: tokens.muted }}>
            To confirm, type the membership ID <strong style={{ color: tokens.ink }}>{member.member_id}</strong>.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label={`Type ${member.member_id} to confirm`}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={deleteSubmitting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleteSubmitting}
            sx={{ borderColor: tokens.border, color: tokens.ink }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              deleteSubmitting ||
              deleteConfirmText.trim() !== String(member.member_id || '').trim()
            }
            onClick={submitPermanentDelete}
            sx={{ bgcolor: tokens.danger, '&:hover': { bgcolor: '#93351f' } }}
          >
            {deleteSubmitting ? 'Deleting…' : 'I understand, delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
